import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowRight, BookOpen, DatabaseZap, Mountain, Play, Plus, Sparkles } from 'lucide-react'

import { fetchJson, healthQuery, isApiError } from '@/api/client'
import { startIndex } from '@/api/jobs'
import { keys } from '@/api/keys'
import { puppetsQuery } from '@/api/puppets'
import { stagesQuery, type StageRow } from '@/api/stages'
import { listStories, type StoryRow } from '@/api/stories'
import { useAiAvailability } from '@/components/AiHealthBanner'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { stageThumb } from '@/features/backgrounds/words'
import { PuppetThumb } from '@/features/puppets/PuppetCard'
import { IDEAS } from '@/features/story/StoryWizard'

/**
 * Home: one big **Make a story**, a few ideas to start from, the latest stories (Play), and a few
 * puppets that are ready to act, and a few of the places stories happen in (Backgrounds). The counts are real: puppets come from `GET /api/skel/puppets`
 * (ready ones only), not a sum of per-world numbers. With an empty catalog the page offers the
 * one thing that helps — refreshing the picture library.
 */

interface IndexStatus {
  running_job_id: number | null
  counts: Partial<Record<'background' | 'live_background' | 'character' | 'object' | 'audio', number>>
}

function fetchIndexStatus(signal?: AbortSignal) {
  return fetchJson<IndexStatus>('/api/index/status', { signal })
}

const RECENT_STORIES = 4
const HOME_PUPPETS = 5
const HOME_PLACES = 3

/** A few places worth showing: laid out by hand first, then ones stories use, never switched-off ones. */
export function homePlaces(rows: readonly StageRow[], n = HOME_PLACES): StageRow[] {
  const usable = rows.filter((r) => r.enabled && r.flags.length === 0 && r.annotation.published_rev !== null)
  const custom = usable.filter((r) => r.scenery.mode === 'custom')
  return [...custom, ...usable.filter((r) => r.scenery.mode !== 'custom')].slice(0, n)
}

export function HomePage() {
  const qc = useQueryClient()
  const health = useQuery(healthQuery)
  const status = useQuery({ queryKey: keys.index.status(), queryFn: ({ signal }) => fetchIndexStatus(signal), retry: false, refetchInterval: 15_000 })
  const stories = useQuery({ queryKey: keys.stories.list({ home: true }), queryFn: ({ signal }) => listStories(undefined, signal), retry: false })
  const puppets = useQuery(puppetsQuery({ ready: true }))
  const stages = useQuery({ ...stagesQuery({}), retry: false })
  const { available: aiAvailable, mode: aiMode } = useAiAvailability()

  const c = status.data?.counts
  const total = c ? Object.values(c).reduce<number>((n, v) => n + (v ?? 0), 0) : 0
  const catalogEmpty = status.isSuccess ? total === 0 : false
  const indexing = status.data?.running_job_id != null

  const index = useMutation({
    mutationFn: () => startIndex({ mode: 'full' }),
    onSuccess: ({ job_id }) => {
      toast.success('Refreshing the picture library', { description: `Job #${job_id} started.` })
      void qc.invalidateQueries({ queryKey: keys.index.all() })
    },
    onError: (err) => {
      if (isApiError(err) && err.isConflict) return void toast.info('Already refreshing', { description: 'Watch the strip at the bottom.' })
      toast.error('Could not refresh the picture library', { description: err instanceof Error ? err.message : String(err) })
    },
  })

  const recent = (stories.data?.items ?? []).filter((s) => s.status === 'ready').slice(0, RECENT_STORIES)
  const ready = puppets.data?.items.filter((p) => p.story_ready) ?? []
  const places = homePlaces(stages.data?.items ?? [])

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-10 px-4 py-10 sm:py-14">
      <section className="flex flex-col items-center gap-5 text-center" aria-labelledby="home-title">
        <h1 id="home-title" className="text-4xl font-semibold tracking-tight text-ink sm:text-5xl">
          Story Studio
        </h1>
        <p className="max-w-xl text-base text-ink-2 sm:text-lg">Pick a few friends, type a little idea, and watch them act it out.</p>
        {catalogEmpty && !indexing ? (
          <EmptyCatalog onRefresh={() => index.mutate()} busy={index.isPending} serverDown={health.isError} />
        ) : (
          <>
            <Link
              to="/stories/new"
              className="group inline-flex min-h-20 w-full max-w-md items-center justify-center gap-3 rounded-3xl bg-accent px-8 text-2xl font-semibold text-accent-ink shadow-card transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-pop focus-visible:focus-ring active:translate-y-0"
              data-testid="home-make-story"
            >
              <Sparkles className="size-7" aria-hidden />
              Make a story
            </Link>
            <p className="text-sm text-ink-2">
              {indexing
                ? 'The picture library is being refreshed — new friends appear as they are found.'
                : !aiAvailable && aiMode === 'down'
                  ? 'The story helper is asleep right now — saved stories still play.'
                  : puppets.isSuccess
                    ? `${ready.length} ${ready.length === 1 ? 'puppet is' : 'puppets are'} ready to act — any character can join.`
                    : ' '}
            </p>
            <div className="flex max-w-3xl flex-wrap justify-center gap-2" aria-label="Ideas to start from">
              {IDEAS.slice(0, 3).map((idea) => (
                <Link
                  key={idea}
                  to="/stories/new"
                  search={{ idea }}
                  className="min-h-10 rounded-2xl border border-line bg-paper px-3 py-2 text-sm text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:focus-ring"
                >
                  {idea}
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="home-stories">
        <div className="flex items-end justify-between gap-3">
          <h2 id="home-stories" className="text-xl font-semibold text-ink">
            Your stories
          </h2>
          <Link to="/stories" className="inline-flex min-h-10 items-center gap-1 rounded-lg px-2 text-sm font-medium text-accent hover:underline">
            All stories
            <ArrowRight className="size-4" aria-hidden />
          </Link>
        </div>
        {recent.length === 0 ? (
          <p className="card p-6 text-ink-2">{stories.isPending ? 'Looking for your stories…' : 'No stories yet — your first one is one tap away.'}</p>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2" role="list">
            {recent.map((s) => (
              <StoryTile key={s.uid} story={s} />
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="home-puppets">
        <div className="flex items-end justify-between gap-3">
          <h2 id="home-puppets" className="text-xl font-semibold text-ink">
            Ready to act
          </h2>
          <Link to="/puppets" className="inline-flex min-h-10 items-center gap-1 rounded-lg px-2 text-sm font-medium text-accent hover:underline">
            All puppets
            <ArrowRight className="size-4" aria-hidden />
          </Link>
        </div>
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-3" role="list" data-testid="home-puppets">
          {ready.slice(0, HOME_PUPPETS).map((p) => (
            <li key={p.uid}>
              <Link to="/stories/new" search={{ cast: p.uid }} className="card flex flex-col overflow-hidden transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-pop focus-visible:focus-ring" aria-label={`Make a story with ${p.name}`}>
                <PuppetThumb src={p.thumb_url} name={p.name} />
                <span className="truncate px-3 py-2 text-sm font-semibold text-ink">{p.name}</span>
              </Link>
            </li>
          ))}
          <li>
            <Link to="/puppets" className="flex aspect-[4/5] h-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-line-strong p-3 text-center text-sm font-medium text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:focus-ring">
              <Plus className="size-6" aria-hidden />
              Make a puppet move
            </Link>
          </li>
        </ul>
      </section>

      {places.length > 0 ? (
        <section className="flex flex-col gap-3" aria-labelledby="home-places">
          <div className="flex items-end justify-between gap-3">
            <h2 id="home-places" className="text-xl font-semibold text-ink">
              Places to play
            </h2>
            <Link to="/backgrounds" className="inline-flex min-h-10 items-center gap-1 rounded-lg px-2 text-sm font-medium text-accent hover:underline">
              All backgrounds
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          </div>
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(12rem,1fr))] gap-3" role="list" data-testid="home-places">
            {places.map((r) => (
              <li key={r.uid}>
                <Link to="/backgrounds/$" params={{ _splat: r.uid }} className="card flex flex-col overflow-hidden transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-pop focus-visible:focus-ring" aria-label={`Open ${r.name}`}>
                  <img src={stageThumb(r.uid, r)} alt="" loading="lazy" className="aspect-video w-full bg-paper-2 object-cover" />
                  <span className="truncate px-3 py-2 text-sm font-semibold text-ink">{r.name}</span>
                </Link>
              </li>
            ))}
            <li>
              <Link to="/backgrounds" className="flex h-full min-h-28 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-line-strong p-3 text-center text-sm font-medium text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:focus-ring">
                <Mountain className="size-6" aria-hidden />
                Dress up a background
              </Link>
            </li>
          </ul>
        </section>
      ) : null}
    </div>
  )
}

function StoryTile({ story }: { story: StoryRow }) {
  return (
    <li className="card flex items-center gap-3 p-3">
      <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-2xl bg-accent-soft text-accent">
        <BookOpen className="size-6" aria-hidden />
      </span>
      <Link to="/stories/$uid" params={{ uid: story.uid }} className="min-w-0 flex-1 rounded-lg focus-visible:focus-ring">
        <span className="block truncate font-semibold text-ink">{story.title}</span>
        <span className="block truncate text-sm text-ink-2">{story.prompt}</span>
      </Link>
      <Button asChild variant="primary" size="md">
        <Link to="/stories/$uid" params={{ uid: story.uid }} search={{ step: 'play' }} aria-label={`Play “${story.title}”`}>
          <Play aria-hidden />
          Play
        </Link>
      </Button>
    </li>
  )
}

function EmptyCatalog({ onRefresh, busy, serverDown }: { onRefresh: () => void; busy: boolean; serverDown: boolean }) {
  return (
    <div className="card flex max-w-lg flex-col items-center gap-4 p-8 text-center">
      <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-accent-soft text-accent">
        <DatabaseZap className="size-7" aria-hidden />
      </span>
      <p className="text-sm text-ink-2">The picture library is empty. Refresh it to bring in the characters, animals, props and places. It takes a few minutes.</p>
      <Button variant="primary" size="xl" onClick={onRefresh} loading={busy} disabled={serverDown}>
        {serverDown ? 'Server unreachable' : 'Refresh the picture library'}
      </Button>
    </div>
  )
}
